//COMPONENTS
import {
  Accordion,
  ActionIcon,
  Alert,
  Autocomplete,
  Badge,
  Button,
  Card,
  Checkbox,
  Chip,
  CloseButton,
  ColorInput,
  Combobox,
  Container,
  createTheme,
  Input,
  Loader,
  Menu,
  MultiSelect,
  NavLink,
  NumberInput,
  Pagination,
  Paper,
  PillsInput,
  Progress,
  Radio,
  ScrollArea,
  Select,
  Slider,
  Stepper,
  Switch,
  Table,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { DatePickerInput, DateTimePicker, TimeInput } from "@mantine/dates";

//ICONS
import {
  Calendar,
  CalendarTime,
  ChevronDown,
  Clock,
  CloseIcon,
  EyeDropper,
} from "./components-icons";

//COLOURS

//STYLES IMPORTS
//mantine
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dropzone/styles.css";

//styles
import "@scss/base-components/variants.scss";
import "@scss/base-components/container.scss";

const theme = createTheme({
  fontFamily: "'basic-sans', sans-serif",

  breakpoints: {
    xs: "30em", // 480px - Mobile portrait
    sm: "48em", // 768px - Mobile landscape / Small tablets
    md: "64em", // 1024px - Tablets / Small laptops
    lg: "75em", // 1200px - Desktop (matches $mobile-breakpoint)
    xl: "88em", // 1408px - Large desktop
  },
  fontSizes: {
    xs: "var(--font-xs)",
    sm: "var(--font-sm)",
    md: "var(--font-md)",
    lg: "var(--font-lg)",
    xl: "var(--font-xl)",
  },
  headings: {
    sizes: {
      h1: { fontSize: "var(--heading-h1)" },
      h2: { fontSize: "var(--heading-h2)" },
      h3: { fontSize: "var(--heading-h3)" },
      h4: { fontSize: "var(--heading-h4)" },
      h5: { fontSize: "var(--heading-h5)" },
      h6: { fontSize: "var(--heading-h6)" },
    },
  },
  components: {
    Accordion: Accordion.extend({
      classNames: {
        root: "accordion",
        item: "item",
        control: "control",
        content: "content",
      },
    }),
    ActionIcon: ActionIcon.extend({
      classNames: {
        root: "action-icon",
      },
    }),
    Alert: Alert.extend({
      classNames: {
        root: "alert",
        title: "title",
        message: "message",
      },
    }),
    Autocomplete: Autocomplete.extend({
      defaultProps: {
        variant: "default",
      },
      classNames: {
        root: "autocomplete",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
        dropdown: "dropdown",
        options: "options",
        option: "option",
      },
    }),
    Badge: Badge.extend({
      defaultProps: {
        variant: "green",
      },
      classNames: {
        root: "badge",
        section: "section",
      },
    }),
    Button: Button.extend({
      defaultProps: {
        variant: "primary",
      },
      classNames: {
        root: "button",
        inner: "inner",
        section: "section",
        loader: "loader",
        label: "label",
      },
    }),
    Card: Card.extend({
      classNames: {
        root: "card",
      },
    }),
    Container: Container.extend({
      classNames: {
        root: "container",
      },
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        variant: "green",
      },
      classNames: {
        root: "checkbox",
        input: "input",
        icon: "checkbox-icon",
      },
    }),
    Chip: Chip.extend({
      defaultProps: {
        variant: "primary",
      },
      classNames: {
        root: "chip",
        input: "input",
        label: "label",
        iconWrapper: "icon",
      },
    }),
    CloseButton: CloseButton.extend({
      defaultProps: {
        icon: CloseIcon,
      },
      classNames: {
        root: "close-button",
      },
    }),
    ColorInput: ColorInput.extend({
      defaultProps: {
        variant: "default",
        rightSection: EyeDropper,
      },
      classNames: {
        root: "color-input",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
      },
    }),
    Combobox: Combobox.extend({
      classNames: {
        dropdown: "dropdown",
        options: "options",
        option: "option",
        search: "search",
      },
    }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        variant: "default",
        rightSection: Calendar,
      },
      classNames: {
        root: "date-picker",
        wrapper: "wrapper",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
      },
    }),
    DateTimePicker: DateTimePicker.extend({
      defaultProps: {
        variant: "default",
        rightSection: CalendarTime,
      },
      classNames: {
        root: "date-time-picker",
        wrapper: "wrapper",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
      },
    }),
    Dropzone: Dropzone.extend({
      classNames: {
        root: "dropzone",
      },
    }),
    Input: Input.extend({
      defaultProps: {
        variant: "default",
      },
      classNames: {
        wrapper: "input-field",
        input: "input",
        section: "section",
      },
    }),
    Loader: Loader.extend({
      classNames: {
        root: "loader",
      },
    }),
    Menu: Menu.extend({
      classNames: {
        dropdown: "menu-dropdown",
      },
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        variant: "default",
        rightSection: ChevronDown,
      },
      classNames: {
        root: "multi-select",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
        dropdown: "dropdown",
        options: "options",
        option: "option",
        pill: "pill",
        pillsList: "pills-list",
      },
    }),
    NavLink: NavLink.extend({
      defaultProps: {
        variant: "primary",
      },
      classNames: {
        root: "navLink",
        section: "section",
        body: "body",
      },
    }),
    NumberInput: NumberInput.extend({
      defaultProps: {
        variant: "default",
      },
      classNames: {
        root: "number-input",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
        control: "control",
      },
    }),
    Pagination: Pagination.extend({
      classNames: {
        root: "pagination",
        control: "control",
      },
    }),
    Paper: Paper.extend({
      classNames: {
        root: "paper",
      },
    }),
    PillsInput: PillsInput.extend({
      defaultProps: {
        variant: "default",
      },
      classNames: {
        root: "pills-input",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
      },
    }),
    Progress: Progress.extend({
      defaultProps: {
        variant: "green",
        size: "xs",
      },
      classNames: {
        root: "progress",
      },
    }),
    Radio: Radio.extend({
      defaultProps: {
        variant: "green",
      },
      classNames: {
        root: "radiobutton",
        radio: "radio",
        icon: "radio-icon",
      },
    }),
    ScrollArea: ScrollArea.extend({
      defaultProps: {
        offsetScrollbars: true,
        scrollbarSize: 8,
      },
    }),
    Select: Select.extend({
      defaultProps: {
        variant: "default",
        rightSection: ChevronDown,
        rightSectionPointerEvents: "none",
      },
      classNames: {
        root: "select",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
      },
    }),
    Slider: Slider.extend({
      defaultProps: {
        variant: "green",
        size: "sm",
      },
      classNames: {
        root: "slider",
        track: "track",
        bar: "bar",
        thumb: "thumb",
        label: "label",
      },
    }),
    Stepper: Stepper.extend({
      defaultProps: {
        variant: "green",
        size: "sm",
      },
      classNames: {
        root: "stepper",
        steps: "steps",
        step: "step",
        stepBody: "step-body",
        stepLabel: "step-label",
        stepDescription: "step-description",
        stepIcon: "step-icon",
        stepCompletedIcon: "step-completed-icon",
        separator: "separator",
        verticalSeparator: "vertical-separator",
        stepWrapper: "step-wrapper",
      },
    }),
    Switch: Switch.extend({
      defaultProps: {
        variant: "green",
      },
      classNames: {
        root: "switch",
        input: "input",
        track: "track",
        thumb: "thumb",
      },
    }),
    Table: Table.extend({
      defaultProps: {
        variant: "default",
      },
      classNames: {
        table: "table",
        th: "th",
        td: "td",
        tr: "tr",
        caption: "caption",
      },
    }),
    TagsInput: TagsInput.extend({
      defaultProps: {
        variant: "default",
      },
      classNames: {
        root: "tags-input",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
        dropdown: "dropdown",
        options: "options",
        option: "option",
        pill: "pill",
        pillsList: "pills-list",
      },
    }),
    Text: Text.extend({
      classNames: {
        root: "text",
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        variant: "default",
      },
      classNames: {
        root: "text-input",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        variant: "default",
      },
      classNames: {
        root: "textarea",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
      },
    }),

    TimeInput: TimeInput.extend({
      defaultProps: {
        variant: "default",
        rightSection: Clock,
      },
      classNames: {
        root: "time-input",
        input: "input",
        error: "error",
        label: "label",
        required: "required",
        section: "section",
        wrapper: "wrapper",
      },
    }),

    Title: Title.extend({
      classNames: {
        root: "title",
      },
    }),
    Tooltip: Tooltip.extend({
      defaultProps: {
        withArrow: true,
      },
      classNames: {
        tooltip: "tooltip",
      },
    }),
  },
});

export default theme;
